<script setup lang="ts">
import type { SizeOption, SizeVariant } from '~~/shared/types/store'

const props = defineProps<{
  variants: SizeVariant[]
  fallbackSizes: SizeOption[]
  selectedSize: string
}>()

const emit = defineEmits<{
  select: [size: string]
}>()

const useVariants = computed(() => props.variants.length > 0)
</script>

<template>
  <div class="size-selector">
    <!-- allegrohash variants: in-place swap via API, no route navigation -->
    <template v-if="useVariants">
      <template v-for="v in variants" :key="v.id">
        <button
          v-if="v.available"
          type="button"
          class="size-btn"
          :class="{ 'size-btn--active': v.isCurrent }"
          :aria-pressed="v.isCurrent"
          @click="emit('select', v.rozmiar)"
        >
          {{ v.rozmiar }}
        </button>
        <span
          v-else
          class="size-btn size-btn--unavailable"
          :aria-disabled="true"
        >
          {{ v.rozmiar }}
        </span>
      </template>
    </template>

    <!-- fallback: standard combinations -->
    <template v-else>
      <button
        v-for="size in fallbackSizes"
        :key="size.label"
        type="button"
        class="size-btn"
        :class="{
          'size-btn--active': selectedSize === size.label,
          'size-btn--unavailable': !size.available,
        }"
        :disabled="!size.available"
        @click="emit('select', size.label)"
      >
        {{ size.label }}
      </button>
    </template>
  </div>
</template>

<style scoped>
.size-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.size-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 40px;
  padding: 0 12px;
  border: 0;
  border-bottom: 2px solid var(--color-border, #e0e0e0);
  border-radius: var(--size-option-radius);
  background: transparent;
  font-size: 1.3rem;
  font-family: var(--font-body);
  font-weight: var(--font-weight-light);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.size-btn:hover:not(.size-btn--unavailable) {
  border-color: var(--color-primary, #111);
}

.size-btn--active {
  border-bottom-color: var(--color-primary, #111);
  color: var(--color-primary, #111);
  font-weight: var(--font-weight-medium);
}

.size-btn--active:hover {
  color: var(--color-primary, #111);
}

.size-btn--unavailable {
  color: var(--color-muted);
  opacity: 0.28;
  cursor: not-allowed;
}

/* youlook (#3): opcje rozmiaru jako zaokrąglony przycisk z ramką,
   zamiast podkreślenia. Ten sam markup, różnica czysto wizualna. */
[data-brand="youlook"] .size-btn {
  border: 1px solid var(--color-border-md, #c7c7c7);
  border-bottom: 1px solid var(--color-border-md, #c7c7c7);
}
[data-brand="youlook"] .size-btn--active {
  border-color: var(--color-black);
  background: var(--color-black);
  color: var(--color-white);
}
</style>
